import { useState } from 'react';
import { fetchJobsByTask, isOffQueueStatus } from '../../lib/aiJobs';
import { enqueueAiJobWithFiles } from '../../lib/aiJobAttachments';
import { useUser } from '../../context/UserContext';
import { Button, IconButton, SidePanel } from '../ui';
import TrelloCardDetailBody from '../comms/TrelloCardDetailBody';
import SpecQueueNoteFields from './SpecQueueNoteFields';
import NoteRequirementsAlert from './NoteRequirementsAlert';
import { useAttachmentPicker } from './attachmentPicker';
import { unmetRequirements, type NoteTemplate } from '../../lib/noteTemplates';

type Trigger = 'bolt' | 'button';

interface SpecQueueFromTargetProps {
  taskId: string;
  title: string;
  /** null = task description only */
  cardId: string | null;
  trigger?: Trigger;
  onEnqueued?: () => void;
}

export default function SpecQueueFromTarget({
  taskId,
  title,
  cardId,
  trigger = 'bolt',
  onEnqueued,
}: SpecQueueFromTargetProps) {
  const { user } = useUser();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const [template, setTemplate] = useState<NoteTemplate | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const attachments = useAttachmentPicker();

  const reset = () => {
    setOpen(false);
    setNote('');
    setTemplate(null);
    setModel(null);
    attachments.clear();
    setError(null);
  };

  const handleOpen = async () => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const jobs = await fetchJobsByTask(taskId);
      const openJobs = jobs.filter((j) => !isOffQueueStatus(j.status));
      const already = openJobs.some((j) => (j.card_id || null) === (cardId || null));
      if (already) {
        setError('This item is already in the agent queues');
        setOpen(true);
        return;
      }
      setNote('');
      setTemplate(null);
      setModel(null);
      attachments.clear();
      setOpen(true);
    } catch (err) {
      console.error('Error checking agent queues:', err);
      setError(err instanceof Error ? err.message : 'Failed to open spec queue');
      setOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (busy) return;
    // The picked canned note's rules, checked before anything is created. The database checks again.
    const unmet = unmetRequirements(template, { attachments: attachments.files.length });
    if (unmet.length) {
      setMissing(unmet);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await enqueueAiJobWithFiles(
        {
          task_id: taskId,
          title,
          card_id: cardId,
          created_by: user?.id ?? null,
          queue_note: note.trim(),
          model,
          note_template_id: template?.id ?? null,
        },
        attachments.files.map((f) => f.file)
      );
      reset();
      onEnqueued?.();
    } catch (err) {
      console.error('Error enqueueing AI job:', err);
      setError(err instanceof Error ? err.message : 'Failed to enqueue spec');
    } finally {
      setBusy(false);
    }
  };

  const hint = cardId ? 'Bound to this comms chip' : 'Spec from the task description — not a comms card';
  const busyLabel = attachments.files.length > 0 ? 'Uploading…' : 'Queuing…';

  return (
    <>
      {trigger === 'button' ? (
        <Button size="sm" variant="secondary" icon="zap" onClick={handleOpen} disabled={busy}>
          {busy ? 'Queuing…' : 'Spec Queue'}
        </Button>
      ) : (
        <IconButton
          icon="zap"
          size="sm"
          label="Queue this as an agent spec"
          onClick={(e) => {
            e.stopPropagation();
            handleOpen();
          }}
          disabled={busy}
        />
      )}

      <SidePanel
        open={open}
        onClose={() => {
          if (!busy) reset();
        }}
        eyebrow="Agent Spec Queue"
        title={title}
        width={480}
        layer={2}
        footer={
          <>
            <Button onClick={handleConfirm} disabled={busy || !!error} icon="zap">
              {busy ? busyLabel : 'Confirm'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                if (!busy) reset();
              }}
            >
              Cancel
            </Button>
          </>
        }
      >
        <p style={{ margin: '0 0 14px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {hint}. Add a note and any files for Owlbert, then confirm.
        </p>
        <SpecQueueNoteFields
          note={note}
          onNoteChange={setNote}
          template={template}
          onTemplateChange={setTemplate}
          picker={attachments}
          disabled={busy}
          model={model}
          onModelChange={setModel}
        />
        {error ? (
          <div
            style={{
              marginTop: '14px',
              padding: '10px 12px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {error}
          </div>
        ) : null}

        {/* The card as Owlbert will read it — same live Trello state the comms
            detail panel shows, so the note can be written against it */}
        {cardId && open ? (
          <div style={{ marginTop: '18px' }}>
            <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Card context</div>
            <p style={{ margin: '0 0 10px', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Owlbert reads this card from Trello when it picks up the job.
            </p>
            <TrelloCardDetailBody cardId={cardId} />
          </div>
        ) : null}
      </SidePanel>
      <NoteRequirementsAlert
        open={missing.length > 0}
        templateName={template?.name ?? 'canned'}
        missing={missing}
        onClose={() => setMissing([])}
      />
    </>
  );
}
